import { FileTypeEnum } from '../../enums/FileTypeEnum';
import type { SharePointEditSession } from '../../interfaces/SharePointApiService';
import { envFlag } from '../envFlag';

/** Query string flag added by the SharePoint button: tells the viewer where the file comes from. */
export const SHAREPOINT_VIEWER_PARAM = 'sharepoint';

/**
 * Whether the SharePoint integration is enabled for this deployment
 * (`VITE_APP_SHAREPOINT_ENABLED`). Disabled by default: it is a preview feature.
 *
 * Read through {@link envFlag}, which is what keeps the flag switchable at container start —
 * see that helper for why an inline `=== 'true'` comparison cannot work here.
 */
export function isSharePointEnabled(): boolean {
  return envFlag(import.meta.env.VITE_APP_SHAREPOINT_ENABLED);
}

/**
 * Encodes the absolute URL of a SharePoint file as the id used by `/viewer/{id}?sharepoint=true`.
 *
 * The value is the base64url of the URL, i.e. exactly the Graph sharing token (without the
 * `u!` prefix), so the backend resolves the file with a single `/shares/u!{id}/driveItem`
 * call. base64url only uses `A-Za-z0-9-_`, so spaces, accents, `&` and `#` in file names are
 * a non-issue inside a path segment.
 *
 * The id is NOT a capability: it carries no token and no key, and authorization always
 * happens later against the user's own delegated token.
 */
export function toSharePointShareId(absoluteFileUrl: string): string {
  // TextEncoder before btoa: accented file names would otherwise throw.
  const bytes = new TextEncoder().encode(absoluteFileUrl);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decodes a share id back to the absolute file URL (used to show the path to the user). */
export function fromSharePointShareId(shareId: string): string | null {
  try {
    const padded = shareId.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/** Extension → viewer type. Mirrors the conversions the PDF processing service applies. */
const FILE_TYPE_BY_EXTENSION: Record<string, FileTypeEnum> = {
  ai: FileTypeEnum.Pdf,
  bmp: FileTypeEnum.Image,
  csv: FileTypeEnum.Excel,
  doc: FileTypeEnum.Word,
  docm: FileTypeEnum.Word,
  docx: FileTypeEnum.Word,
  dot: FileTypeEnum.Word,
  dotx: FileTypeEnum.Word,
  dwg: FileTypeEnum.Pdf,
  epub: FileTypeEnum.Pdf,
  gif: FileTypeEnum.Image,
  jpeg: FileTypeEnum.Image,
  jpg: FileTypeEnum.Image,
  odt: FileTypeEnum.Word,
  pages: FileTypeEnum.Pdf,
  pdf: FileTypeEnum.Pdf,
  png: FileTypeEnum.Image,
  pot: FileTypeEnum.Powerpoint,
  potx: FileTypeEnum.Powerpoint,
  pps: FileTypeEnum.Powerpoint,
  ppsx: FileTypeEnum.Powerpoint,
  ppt: FileTypeEnum.Powerpoint,
  pptx: FileTypeEnum.Powerpoint,
  psd: FileTypeEnum.Pdf,
  rtf: FileTypeEnum.Word,
  svg: FileTypeEnum.Image,
  tif: FileTypeEnum.Image,
  tiff: FileTypeEnum.Image,
  webp: FileTypeEnum.Image,
  xls: FileTypeEnum.Excel,
  xlsm: FileTypeEnum.Excel,
  xlsx: FileTypeEnum.Excel,
  xlt: FileTypeEnum.Excel,
  xltx: FileTypeEnum.Excel,
};

/** Extensions the viewer renders as plain text. */
const TEXT_EXTENSIONS = new Set([
  'css',
  'htm',
  'html',
  'json',
  'log',
  'md',
  'txt',
  'xml',
  'yaml',
  'yml',
]);

/** File extension (without the dot, lowercase) of a SharePoint file name. */
export function sharePointFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot < 0 ? '' : fileName.slice(lastDot + 1).toLowerCase();
}

/**
 * Viewer type for a SharePoint file, derived from its name: Office documents are converted
 * to PDF by the service, so they render in the PDF viewer.
 */
export function sharePointFileType(fileName: string): FileTypeEnum {
  const extension = sharePointFileExtension(fileName);
  if (extension === '') return FileTypeEnum.Unknown;
  if (TEXT_EXTENSIONS.has(extension)) return FileTypeEnum.Text;
  return FILE_TYPE_BY_EXTENSION[extension] ?? FileTypeEnum.Unknown;
}

/** True when the decrypted content is delivered as a PDF (either native or converted). */
export function isSharePointPdfRendered(fileType: FileTypeEnum): boolean {
  return [
    FileTypeEnum.Excel,
    FileTypeEnum.Pdf,
    FileTypeEnum.Powerpoint,
    FileTypeEnum.Word,
  ].includes(fileType);
}

/**
 * Extensions an office provider can EDIT. Not the same question as which viewer renders a file: the
 * legacy binary formats (.doc, .xls, .ppt) and PDFs are viewable but not editable.
 *
 * A copy of the list the API enforces (`SharePointEditSessionService.EditableExtensions`), kept here only
 * to decide whether to offer the Edit button. The API is the authority and refuses anything else with
 * `SHAREPOINT_EDIT_UNSUPPORTED_TYPE`, so the worst a drift here can cause is a button that leads to a
 * clear error — never an unsupported document reaching an editor.
 */
const EDITABLE_EXTENSIONS = new Set([
  'docm',
  'docx',
  'dotm',
  'dotx',
  'potx',
  'ppsx',
  'pptm',
  'pptx',
  'xlsm',
  'xlsx',
  'xltm',
  'xltx',
]);

/** True when the file can be opened in the office editor rather than only in the viewer. */
export function isSharePointEditable(fileName: string): boolean {
  return EDITABLE_EXTENSIONS.has(sharePointFileExtension(fileName));
}

/**
 * The url the editing session's iframe is POSTed to — which editor actually opens the SharePoint file.
 *
 * The deployment's `OFFICE-PROVIDER` decides, and the API answers with it: for a self-hosted provider
 * (ONLYOFFICE Docs, Collabora Online) it resolves the editor url from that server's WOPI discovery and
 * sends it in `editorUrl`, because the browser cannot fetch discovery cross-origin. Only Office Online
 * needs the client to build a url, from the fixed frame addresses in `VITE_APP_OFFICE_*_URL`.
 *
 * An empty result means no editor can open this file: pass `''` as `msOfficeBaseUrl` for a file type
 * Office Online has no frame for, and a self-hosted provider that sent no url has nothing to fall back
 * to — sending a customer's SharePoint file to Office Online instead would be the wrong answer, not a
 * lenient one.
 *
 * @param msOfficeBaseUrl {@link getOfficeFileBaseUrl} for this file type and mode; ignored unless the
 *   provider is Office Online. Passed in, rather than imported, to keep this module free of the app's
 *   component graph.
 * @param officeCulture Full BCP 47 locale ('it-IT'), which is what Office Online wants.
 */
export function sharePointEditorFrameUrl(
  session: Pick<SharePointEditSession, 'editorUrl' | 'provider' | 'wopiSrc'>,
  msOfficeBaseUrl: string,
  officeCulture: string,
): string {
  // Self-hosted provider: urlsrc + WOPISrc, already assembled by the API. Its query is the provider's
  // own — the Office Online parameters below mean nothing there and are not added.
  if (session.editorUrl != null && session.editorUrl !== '') return session.editorUrl;

  // Provider set to something other than Office Online, yet no url came with it.
  if (session.provider != null && session.provider !== 'msoffice') return '';

  if (msOfficeBaseUrl === '') return '';

  return (
    `${msOfficeBaseUrl}wopisrc=${encodeURIComponent(session.wopiSrc)}` +
    `&IsLicensedUser=1&ui=${officeCulture}&rs=${officeCulture}`
  );
}
